/**
 * Scenario schemas — Sprint 14.
 *
 * A scenario is a named set of partial input overrides applied on top of
 * an analysis's base inputs. Up to 3 scenarios are compared side-by-side
 * in the comparison dashboard.
 *
 * Design contract:
 *   - ScenarioOverrides is a fully-optional subset of the wizard steps
 *   - The API merges overrides with the base analysis inputs at run-time
 *   - Only incentive, rate, and job parameters are overridable in v1
 *     (the fields analysts most commonly vary across proposals)
 */

import { z } from 'zod';

/**
 * Partial input overrides an analyst can vary in a scenario.
 * All fields are optional — only the overridden fields need to be present.
 *
 * Grouped to match wizard step semantics:
 *   incentive — step8 fields (grant amounts, abatement %, years)
 *   rates     — step6 district tax rates
 *   jobs      — step3 direct employment
 */
export const ScenarioOverrides = z
  .object({
    // ── Incentive package (Step 8) ──────────────────────────────────────
    /** Job creation grant as % of annual payroll (0..1). */
    jobGrantPctOfPayroll: z.number().min(0).max(1).optional(),
    /** Number of years the job grant applies. */
    jobGrantYears: z.number().int().min(0).max(20).optional(),
    /** One-time infrastructure grant total ($). */
    infrastructureGrantTotal: z.number().nonnegative().optional(),
    /** Years over which the infrastructure grant is paid. */
    infrastructureGrantYears: z.number().int().min(1).max(20).optional(),
    /** Property tax abatement fraction (0..1). */
    propertyTaxAbatementPct: z.number().min(0).max(1).optional(),
    /** Years of property tax abatement. */
    propertyTaxAbatementYears: z.number().int().min(0).max(20).optional(),
    /** Stepped/declining abatement schedule — one fraction (0..1) per year; overrides the flat pct/years pair above when present. */
    propertyTaxAbatementSchedule: z.array(z.number().min(0).max(1)).max(30).optional(),
    /** Discount rate for NPV calculation (0..1). */
    discountRate: z.number().min(0).max(0.3).optional(),

    // ── Tax rates (Step 6 — district subset) ────────────────────────────
    /** County property tax rate, decimal. */
    countyPropertyTaxRate: z.number().min(0).max(0.05).optional(),
    /** ISD property tax rate, decimal. */
    isdPropertyTaxRate: z.number().min(0).max(0.05).optional(),
    /** City property tax rate, decimal. */
    propertyTaxRate: z.number().min(0).max(0.05).optional(),

    // ── Direct jobs (Step 3) ─────────────────────────────────────────────
    /** Stabilized direct jobs created. */
    directJobs: z.number().positive().optional(),
    /** Annual direct payroll ($). */
    directAnnualPayroll: z.number().positive().optional(),
  })
  .strict();

export type ScenarioOverrides = z.infer<typeof ScenarioOverrides>;

/** Create / update body for a scenario. */
export const ScenarioSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  inputOverrides: ScenarioOverrides,
});

export type ScenarioInput = z.infer<typeof ScenarioSchema>;
